import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './AdminPanel.css';

const AdminPanel = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [actionNotes, setActionNotes] = useState('');
    const [processing, setProcessing] = useState(false);
    const navigate = useNavigate();

    // Fetch applications from backend
    const fetchApplications = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch('http://localhost:8080/candidate-application/applications', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                setApplications(data);
                setError(null);
            } else {
                setError('Failed to fetch applications');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Check authentication
        const token = localStorage.getItem('token');
        const userType = localStorage.getItem('userType');
        
        if (!token || (userType !== 'admin' && userType !== 'super-admin')) {
            navigate('/admin-login');
            return;
        }

        fetchApplications();
    }, [navigate]);

    // Filter applications by status
    const filteredApplications = applications.filter(app => {
        const actualStatus = app.status || 'PENDING';
        return filterStatus === 'ALL' || actualStatus === filterStatus;
    });

    // Handle status update
    const handleStatusUpdate = async (applicationId, newStatus) => {
        try {
            setProcessing(true);
            const token = localStorage.getItem('token');
            const response = await fetch(
                `http://localhost:8080/candidate-application/applications/${applicationId}/status?status=${newStatus}&notes=${encodeURIComponent(actionNotes)}`,
                { 
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.ok) {
                // Refresh applications list
                await fetchApplications();
                setShowModal(false);
                setActionNotes('');
                setSelectedApplication(null);
                alert(`Application ${newStatus.toLowerCase()} successfully!`);
            } else {
                const errorData = await response.json();
                alert(`Failed to update application: ${errorData.message || 'Unknown error'}`);
            }
        } catch (err) {
            alert('Network error: ' + err.message);
        } finally {
            setProcessing(false);
        }
    };

    // Open action modal
    const openActionModal = (application, action) => {
        setSelectedApplication({ ...application, action });
        setActionNotes('');
        setShowModal(true);
    };

    // Get status badge class
    const getStatusBadgeClass = (status) => {
        const actualStatus = status || 'PENDING';
        switch (actualStatus) {
            case 'PENDING': return 'badge-warning';
            case 'APPROVED': return 'badge-success';
            case 'REJECTED': return 'badge-danger';
            default: return 'badge-warning';
        }
    };

    // Format date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Handle logout
    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('userType');
        localStorage.removeItem('userInfo');
        navigate('/admin-login');
    };

    if (loading) {
        return (
            <div className="admin-panel">
                <Header />
                <div className="admin-header">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <h1>Admin Panel</h1>
                                <p>Loading applications...</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="loading-spinner">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="admin-panel">
            <Header />
            <div className="admin-header">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12">
                            <h1>Admin Panel</h1>
                            <p>Manage candidate applications</p>
                            <button className="btn btn-danger logout-btn" onClick={handleLogout}>
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container" style={{ padding: '0 0 50px 0' }}>
                {error && (
                    <div className="alert alert-danger error-alert" role="alert">
                        {error}
                        <button 
                            className="btn btn-sm btn-outline-danger ml-2" 
                            onClick={fetchApplications}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Filter Controls */}
                <div className="filter-controls">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="statusFilter">Filter by Status:</label>
                                <select 
                                    id="statusFilter"
                                    className="form-control" 
                                    value={filterStatus} 
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                >
                                    <option value="ALL">All Applications</option>
                                    <option value="PENDING">Pending</option>
                                    <option value="APPROVED">Approved</option>
                                    <option value="REJECTED">Rejected</option>
                                </select>
                            </div>
                        </div>
                        <div className="col-md-6 d-flex align-items-end">
                            <button 
                                className="btn refresh-button" 
                                onClick={fetchApplications}
                            >
                                <i className="fa fa-refresh mr-2"></i>
                                Refresh Applications
                            </button>
                        </div>
                    </div>
                </div>

                {/* Applications Table */}
                <div className="applications-table">
                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead className="thead-dark">
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Class Level</th>
                                    <th>Application Date</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredApplications.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="no-applications">
                                            <i className="fa fa-inbox"></i>
                                            <div>No applications found</div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredApplications.map((application) => (
                                        <tr key={application.id}>
                                            <td>
                                                <strong>{application.firstName} {application.lastName}</strong>
                                            </td>
                                            <td>{application.email}</td>
                                            <td>{application.classLevel}</td>
                                            <td>{formatDate(application.applicationDate)}</td>
                                            <td>
                                                <span className={`badge status-badge ${getStatusBadgeClass(application.status)}`}>
                                                    {application.status || 'PENDING'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="action-buttons" role="group">
                                                    <button 
                                                        className="btn btn-sm btn-info"
                                                        onClick={() => {
                                                            setSelectedApplication(application);
                                                            setShowModal(true);
                                                        }}
                                                    >
                                                        <i className="fa fa-eye mr-1"></i>
                                                        View
                                                    </button>
                                                    {(!application.status || application.status === 'PENDING') && (
                                                        <>
                                                            <button 
                                                                className="btn btn-sm btn-success"
                                                                onClick={() => openActionModal(application, 'APPROVED')}
                                                            >
                                                                <i className="fa fa-check mr-1"></i>
                                                                Accept
                                                            </button>
                                                            <button 
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => openActionModal(application, 'REJECTED')}
                                                            >
                                                                <i className="fa fa-times mr-1"></i>
                                                                Reject
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Application Details Modal */}
                {showModal && selectedApplication && (
                    <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {selectedApplication.action ? 
                                            `${selectedApplication.action} Application` : 
                                            'Application Details'
                                        }
                                    </h5>
                                    <button 
                                        type="button" 
                                        className="close" 
                                        onClick={() => {
                                            setShowModal(false);
                                            setSelectedApplication(null);
                                            setActionNotes('');
                                        }}
                                    >
                                        <span>&times;</span>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="application-details">
                                                <h6>Personal Information</h6>
                                                <p><strong>Name:</strong> {selectedApplication.firstName} {selectedApplication.lastName}</p>
                                                <p><strong>Email:</strong> {selectedApplication.email}</p>
                                                <p><strong>Phone:</strong> {selectedApplication.phone}</p>
                                                <p><strong>Birth Date:</strong> {selectedApplication.birthDate}</p>
                                                {selectedApplication.facebookLink && (
                                                    <p><strong>Facebook:</strong> 
                                                        <a href={selectedApplication.facebookLink} target="_blank" rel="noopener noreferrer">
                                                            {selectedApplication.facebookLink}
                                                        </a>
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="application-details">
                                                <h6>Academic Information</h6>
                                                <p><strong>Class Level:</strong> {selectedApplication.classLevel}</p>
                                                <p><strong>Application Date:</strong> {formatDate(selectedApplication.applicationDate)}</p>
                                                <p><strong>Status:</strong> 
                                                    <span className={`badge status-badge ${getStatusBadgeClass(selectedApplication.status)} ml-2`}>
                                                        {selectedApplication.status || 'PENDING'}
                                                    </span>
                                                </p>
                                                {selectedApplication.notes && (
                                                    <p><strong>Notes:</strong> {selectedApplication.notes}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mt-3">
                                        <div className="col-12">
                                            <h6>Why Creative Lab?</h6>
                                            <div className="why-creative-lab">
                                                {selectedApplication.whyCreativeLab}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {selectedApplication.action && (
                                        <div className="row mt-3">
                                            <div className="col-12">
                                                <div className="form-group">
                                                    <label htmlFor="actionNotes">
                                                        Notes for {selectedApplication.action.toLowerCase()}:
                                                    </label>
                                                    <textarea 
                                                        id="actionNotes"
                                                        className="form-control" 
                                                        rows="3"
                                                        value={actionNotes}
                                                        onChange={(e) => setActionNotes(e.target.value)}
                                                        placeholder={`Add notes about why this application is being ${selectedApplication.action.toLowerCase()}...`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => {
                                            setShowModal(false);
                                            setSelectedApplication(null);
                                            setActionNotes('');
                                        }}
                                    >
                                        Close
                                    </button>
                                    {selectedApplication.action && (
                                        <button 
                                            type="button" 
                                            className={`btn ${selectedApplication.action === 'APPROVED' ? 'btn-success' : 'btn-danger'}`}
                                            onClick={() => handleStatusUpdate(selectedApplication.id, selectedApplication.action)}
                                            disabled={processing}
                                        >
                                            {processing ? 'Processing...' : `${selectedApplication.action} Application`}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default AdminPanel;
